import { useEffect } from 'react'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { Layout } from './layouts'
import { RecommendationsImageLinksLayout } from './layouts/ImageLinks/RecommendationsImageLinksLayout'
import { RecommendationsTextLinksLayout } from './layouts/TextLinks/RecommendationsTextLinksLayout'
import { RecommendationsTwoByTwoLayout } from './layouts/TwoByTwo/RecommendationsTwoByTwoLayout'
import type { RecommendedContentType, RecommendedModel } from './models'

type LayoutRecommendationsProps = {
  recommendations: RecommendedContentType[]
  whichId: string
  modelId: string
  layout: Layout
  position?: string
  sectionTitle?: string
  consentBannerComponent?: React.ReactNode
}

const LayoutRecommendations = ({
  recommendations,
  whichId,
  modelId,
  layout,
  position,
  sectionTitle = 'Based on your interests',
}: LayoutRecommendationsProps) => {
  useEffect(() => {
    dynamicGa4DataLayerPush({
      event: 'notification_ab_test',
      item_text: `${recommendations.length}`,
      item_parent_text: modelId,
      item_group: 'R4U Recommendation Count',
    })
  }, [recommendations, modelId])

  switch (layout) {
    case Layout.TwoByTwo:
      return (
        <RecommendationsTwoByTwoLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
        />
      )

    case Layout.TextLinks:
      return (
        <RecommendationsTextLinksLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
          position={position}
        />
      )

    case Layout.ImageLinks:
      return (
        <RecommendationsImageLinksLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
          position={position}
        />
      )

    default:
      console.warn(`The '${layout}' recommendation layout is not defined ... />`)
      return null
  }
}

interface RecommendationsProps {
  model: RecommendedModel | null
  whichId: string
  layout: Layout
  position?: string
  sectionTitle?: string
  consentBannerComponent?: React.ReactNode
}

export const Recommendations = ({
  model,
  whichId,
  layout,
  position,
  sectionTitle,
  consentBannerComponent = null,
}: RecommendationsProps) => {
  const { content, model: modelId } = model ?? {}

  if (modelId && content && content.length > 0) {
    return (
      <LayoutRecommendations
        recommendations={content}
        whichId={whichId}
        modelId={modelId}
        layout={layout}
        position={position}
        sectionTitle={sectionTitle}
        consentBannerComponent={consentBannerComponent}
      />
    )
  }

  return null
}
